<template>
  <div id="createPassword" class="w-full">
    <div v-if="isSuccessful">
      <SuccessIcon />
      <p class="font-bold text-base-black text-lg my-3 text-center">Password successfully reset</p>
      <p class="text-grey-light-900 text-sm text-center">
        You can now sign in with your new password.
        <RouterLink class="btn text-cyan-700 p-0 text-sm" to="sign-in" data-testid="go_to_dashboard">
          Go to dashboard.
        </RouterLink>
      </p>
    </div>
    <div v-else>
      <div class="my-3">
        <h5 class="h5 text-lg text-gray-700 font-semibold">Create New Password</h5>
        <p v-if="errorMessage" class="text-red-700">{{ errorMessage }}</p>
      </div>
      <PasswordInput
        id="createPasswordInput"
        v-model="form.password"
        :disabled="isLoading"
        :label="`Enter Password`"
        class="text-left"
      />

      <PasswordInput
        id="createPasswordConfirmInput"
        v-model="confirmPassword"
        :disabled="isLoading"
        :label="`Confirm Password`"
        class="text-left"
      />

      <LayupButton
        :disabled="isLoading"
        data-testid="submit"
        @click="onSubmit()"
        :isLoading="isLoading"
        :label="'Submit'"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import LayupButton from '@/components/common/LayupButton.vue';
import PasswordInput from '@/components/common/PasswordInput.vue';
import SuccessIcon from '@/components/icons/SuccessIcon.vue';
import { NetworkError } from '@/errors';
import { getErrorMessage } from '@/services/auth';
import { updateUser } from '@/services/user';
import { useMerchantStore } from '@/stores/merchants';
import { useUserStore } from '@/stores/users';
import { isValidPassword } from '@/utils/form.utils';
import { addMetric, createException, createWarning } from '@/utils/monitoring.utils';
import { onUnmounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import type { UserServiceApiUserServiceUpdateRequest } from '@layup/api-sdk';

const props = defineProps<{
  channel: string;
}>();

const userStore = useUserStore();
const router = useRouter();

const errorMessage = ref('');
const isLoading = ref(false);
const isSuccessful = ref(false);

const form = {
  cellNumber: '',
  password: '',
};

const confirmPassword = ref('');

const onSubmit = async () => {
  try {
    errorMessage.value = '';
    const passwordValidation = isValidPassword(form.password, confirmPassword.value);
    if (!passwordValidation.isValid) {
      errorMessage.value = passwordValidation.errorMessage;
    } else {
      const payload: UserServiceApiUserServiceUpdateRequest = {
        id: userStore.user._id || '',
        modelUserRequest: {
          password: form.password,
        },
      };
      const user = await updateUser(payload);
      addMetric(`create_password`, { status: 'success', channel: props.channel });
      isSuccessful.value = true;
      userStore.user.verifiedEmail = user.verifiedEmail!;
      userStore.user.email = user.email!;

      // Check if the merchants email has been verified. If not redirect the to a screen that tells them to go check their email.
      if (user.role === 'MERCHANT' && userStore.user.email && !userStore.user.verifiedEmail) {
        router.push({ name: 'merchantVerifyEmail' });
      }

      const merchantStore = useMerchantStore();
      if (!merchantStore.activeMerchantId && userStore.user.merchantIds?.length) {
        merchantStore.activeMerchantId = userStore.user.merchantIds[0];
      }
    }
  } catch (error: any) {
    if (error instanceof NetworkError) {
      createWarning(error);
      addMetric(`create_password`, { status: 'network_error', channel: props.channel });
      errorMessage.value = 'Network Error. Please try again.';
    } else {
      createException(error);
      addMetric(`create_password`, { status: 'error', channel: props.channel });
      errorMessage.value = getErrorMessage(error);
    }
    isLoading.value = false;
  }
};

onUnmounted(() => {
  isLoading.value = false;
});
</script>
